import { useState, useMemo, useEffect, useCallback } from 'react';

import { Search, X, ExternalLink } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCampaigns } from '@/hooks/useCampaigns';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useShiftConfirmations } from '@/hooks/useShiftConfirmations';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/utils/format';

import type {
  ShiftConfirmationQuery,
  ShiftConfirmationType,
  CallWithContact,
} from '@repo/shared-types';

const TABS: { value: ShiftConfirmationType; label: string }[] = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
  { value: 'pending', label: 'Pending' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 records' },
  { value: 25, label: '25 records' },
  { value: 50, label: '50 records' },
  { value: 100, label: '100 records' },
  { value: null, label: 'All records' },
];

const getNotes = (shiftData?: Record<string, unknown> | null): string => {
  if (!shiftData) return '-';
  const notes = shiftData.notes;
  if (typeof notes === 'string' && notes.length > 0) return notes;
  return '-';
};

export const ShiftConfirmationsPage = () => {
  usePageTitle('Shift Confirmations');
  const [searchParams, setSearchParams] = useSearchParams();

  const type = (searchParams.get('type') as ShiftConfirmationType) || 'confirmed';
  const campaignId = searchParams.get('campaign_id') || '';
  const dateFilter = searchParams.get('date') || '';
  const page = Number(searchParams.get('page')) || 1;
  const limitParam = searchParams.get('limit');
  const limit = limitParam === 'all' ? undefined : Number(limitParam) || 10;

  const { data: campaignsData } = useCampaigns({ limit: 100 });

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        }
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const searchChanged = debouncedSearch !== searchInput;
      setDebouncedSearch(searchInput);
      updateParams({
        search: searchInput || null,
        ...(searchChanged && { page: null }),
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams, debouncedSearch]);

  const query = useMemo(() => {
    const q: Partial<ShiftConfirmationQuery> = { page, type };
    if (limit !== undefined) q.limit = limit;
    if (debouncedSearch) q.search = debouncedSearch;
    if (campaignId) q.campaign_id = campaignId;
    if (dateFilter) q.date = dateFilter;
    return q;
  }, [debouncedSearch, campaignId, dateFilter, page, type, limit]);

  const { data, isLoading } = useShiftConfirmations(query);
  const calls = data?.data ?? [];
  const pagination = data?.pagination;

  const hasActiveFilters = searchInput || campaignId || dateFilter;

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      const currentType = prev.get('type');
      if (currentType) next.set('type', currentType);
      const currentLimit = prev.get('limit');
      if (currentLimit) next.set('limit', currentLimit);
      return next;
    });
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
          Shift Confirmations
        </p>
        <h1 className="text-3xl font-bold tracking-tight">Shift Confirmations</h1>
        <p className="text-muted-foreground max-w-3xl">
          Calls related to shift confirmations, split into confirmed, declined, and pending
          (awaiting SMS reply).
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Shift Confirmations</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Tabs */}
          <div className="mb-6 flex gap-1 border-b">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => updateParams({ type: tab.value, page: null })}
                className={cn(
                  '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                  type === tab.value
                    ? 'border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground border-transparent',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-50 flex-1">
              <label className="text-muted-foreground mb-1 block text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="focus:ring-primary w-full rounded-md border py-2 pr-3 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-45">
              <label
                htmlFor="campaign-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Campaign
              </label>
              <select
                id="campaign-filter"
                value={campaignId}
                onChange={(e) => updateParams({ campaign_id: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                <option value="">All campaigns</option>
                {campaignsData?.data.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label
                htmlFor="date-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Date
              </label>
              <input
                id="date-filter"
                type="date"
                value={dateFilter}
                onChange={(e) => updateParams({ date: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              />
            </div>
            <div className="min-w-[120px]">
              <label
                htmlFor="limit-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Per Page
              </label>
              <select
                id="limit-filter"
                value={limit === undefined ? 'all' : limit}
                onChange={(e) =>
                  updateParams({
                    limit: e.target.value === 'all' ? 'all' : e.target.value,
                    page: null,
                  })
                }
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option.value ?? 'all'} value={option.value ?? 'all'}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading shift confirmations...</p>
              </div>
            </div>
          ) : calls.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No {type} shift confirmations found.{' '}
              {hasActiveFilters ? 'Try adjusting your filters.' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2">Contact Name</th>
                    <th className="border-b px-3 py-2">Phone</th>
                    <th className="border-b px-3 py-2">Campaign</th>
                    <th className="border-b px-3 py-2">Notes</th>
                    <th className="border-b px-3 py-2">Call Date</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call: CallWithContact) => (
                    <tr key={call.id} className="hover:bg-muted/50 border-b transition-colors">
                      <td className="px-3 py-2 font-medium">
                        {call.contact?.name || call.contact_id || 'Unknown'}
                      </td>
                      <td className="text-muted-foreground px-3 py-2">
                        {call.phone_number || call.contact?.phone || '-'}
                      </td>
                      <td className="text-muted-foreground px-3 py-2">
                        {call.campaign?.name || '-'}
                      </td>
                      <td className="text-muted-foreground max-w-[200px] truncate px-3 py-2">
                        {getNotes(call.shift_data)}
                      </td>
                      <td className="text-muted-foreground px-3 py-2">
                        {formatDateTime(call.executed_at)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link
                          to={`/calls/${call.id}`}
                          className="text-primary hover:text-primary/80 inline-flex items-center gap-1 text-xs font-medium"
                        >
                          View
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="shift confirmations"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
